import { createTheme } from '@mui/material/styles';
import { red } from '@mui/material/colors';

const fontFamily = "'Gilroy', 'Helvetica Neue', 'Arial', sans-serif";
const DAMSTRA_GREY = '#6f6f6f';
const DAMSTRA_SECONDARY = '#0994ff';
const DAMSTRA_SECONDARY_LIGHT = '#ddf4ff';
const DAMSTRA_ERROR = '#d93240';

const GlobalTheme = createTheme({
  typography: {
    fontFamily,
    htmlFontSize: 14,
  },
  components: {
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          '& .MuiButtonGroup-grouped': {
            borderColor: '#e5e5e5',
          }
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: '#212D38',
        },
        elevation1: {
          boxShadow: '5px 5px 12px rgba(0, 0, 0, 0.06)',
        },
        elevation8: {
          boxShadow: '0 0 12px rgba(0, 0, 0, 0.175)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
            backgroundColor: '#F4F5F6',
            color: '#2f4051',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          lineHeight: '14px',
          '&.Mui-focused': {
            color: 'inherit',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            fontSize: '1rem',
            fontStyle: 'italic',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        // @ts-ignore
        root: {
          color: DAMSTRA_GREY,
          fontWeight: 400,
          fontSize: '1rem',
          top: 'inherit !important',
          left: 'inherit !important',
          position: 'inherit !important',
          transform: 'inherit !important',
          '&.Mui-error': {
            color: DAMSTRA_GREY,
          },
          '&.Mui-focused': {
            color: DAMSTRA_GREY,
          },
        },
        asterisk: {
          '&.Mui-error': {
            color: DAMSTRA_GREY,
          },
        },
        shrink: {
          // Removes default Material UI behaviour of top-left positioning
          // input labels when they are wrapped in a <FormControl />
          fontSize: '1rem',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#212D38',
          fontFamily,
        },
        body1: {
          fontSize: '1rem',
          fontWeight: 400,
        },
        h1: {
          fontSize: 24,
          fontWeight: 500,
        },
        h2: {
          fontSize: 21,
          fontWeight: 500,
        },
        h3: {
          fontSize: 20,
          fontWeight: 500,
        },
        h4: {
          fontSize: 18,
          fontWeight: 400,
        },
        subtitle1: {
          fontSize: 17,
          fontWeight: 400,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          'label + &': {
            marginTop: 8, // Replace `defaultTheme.spacing(1)` with a fixed value
          },
          '&.Mui-error': {
            '& input': {
              borderColor: DAMSTRA_ERROR,
              boxShadow: 'none',
              '&:invalid': {
                boxShadow: 'none',
              },
            },
          },
          padding: '0.64rem 0.75rem',
          fontSize: '1rem',
          fontFamily,
          borderRadius: 4,
          backgroundColor: '#fff',
          border: '1px solid #e5e5e5',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&.Mui-focused': {
            boxShadow: 'rgba(104, 171, 255, 0.25) 0 0 0 0.2rem',
            borderColor: '#68abff',
          },
          '& input': {
            backgroundColor: 'transparent',
            border: 'none',
            color: 'inherit',
            padding: 0,
            margin: 0,
            height: '16.63px',
            '&:focus': {
              border: 'none',
              boxShadow: 'none !important',
            },
          },
          '& adornedStart': {
            height: '24px',
          },
        },
        input: {
          padding: 0,
        },
        multiline: {
          padding: '0.64rem 0.75rem',
        },
        adornedStart: {
          '& svg': {
            color: DAMSTRA_GREY,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out 0s',
          textTransform: 'none',
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          '&:hover': {
            boxShadow: 'none',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid #1233C1',
            outlineOffset: '2px',
            boxShadow: 'none',
            zIndex: 1,
          },
        },
        sizeMedium: {
          padding: '0.64rem 0.75rem',
        },
        contained: {
          // Default buttons (grey)
          fontWeight: 400,
          backgroundColor: '#F4F5F6',
          color: '#212D38',
          '& .MuiSvgIcon-root, & .fa, & .fal, & .far, & .fas': {
            color: '#212D38',
          },
          '&:hover': {
            backgroundColor: '#DDE0E3',
          },
          '&:active': {
            backgroundColor: '#C8CCD1',
          },
          '&.Mui-disabled': {
            backgroundColor: '#DDE0E3',
            color: '#8B939A',
            '& .MuiSvgIcon-root, & .fa, & .fal, & .far, & .fas': {
              color: '#8B939A',
            },
          },
        },
        containedPrimary: {
          // Primary buttons (Save, create, publish, etc)
          backgroundColor: '#1B838B',
          color: '#ffffff',
          '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiSvgIcon-root': {
            color: '#ffffff',
          },
          '&:hover': {
            backgroundColor: '#22A3AF',
          },
          '&:active': {
            backgroundColor: '#056D78',
          },
        },
        textPrimary: { color: '#212D38' },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#056D78',
          textDecoration: 'none',
          '&:visited': {
            color: '#81003F',
          },
          '&:hover': {
            color: '#1B838B',
          },
          '&.Mui-focusVisible': {
            color: '#056D78',
            outline: '2px solid #1233C1',
            outlineOffset: '1px',
            borderRadius: '6px',
          },
          '&:active': {
            color: '#004F58',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        button: {
          '&:hover': {
            backgroundColor: DAMSTRA_SECONDARY_LIGHT,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: '40px',
          margin: '8px',
          padding: '0px 8px',
          borderRadius: '8px',
          '&.Mui-focusVisible': {
            outline: '2px solid #1233C1',
            backgroundColor: 'inherit',
          },
          '&:hover': {
            backgroundColor: '#DDE0E3',
          },
          '&[data-active="true"]': {
            backgroundColor: '#1B838B',
            '& .MuiListItemIcon-root': { color: '#FFFFFF' },
            '& .MuiListItemText-primary': { color: '#FFFFFF' },
          },
          '& .MuiListItemIcon-root': {
              padding: '0px',
              minWidth: '40px',
            },
          '& .MuiListItemText-primary': {
            fontWeight: 500,
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          '.MuiDrawer-root &': {
            margin: '0px 8px',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f6fc',
        },
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #e6eaec',
          borderRight: '1px solid #e6eaec',
          borderLeft: '1px solid #e6eaec',
          background: 'whitesmoke',
          textTransform: 'none',
          fontWeight: 400,
          '&.Mui-selected': {
            color: '#001e35',
            background: '#fff',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: '#fafafd',
          border: '1px solid #e0e0e0',
          borderBottom: 'none',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& tr': {
            border: '1px solid #e0e0e0',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          border: 'none',
        },
        spacer: {
          flex: 'inherit',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        li: {
          '& a': { color: DAMSTRA_GREY },
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#00000014 !important',
            '&:hover': {
              backgroundColor: '#00000014',
            },
          },
          '&:hover': {
            backgroundColor: DAMSTRA_SECONDARY_LIGHT,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#0994ff14',
            '&:hover': {
              backgroundColor: '#0994ff14',
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          '&.Mui-focused[aria-selected="true"]': {
            backgroundColor: '#00000014 !important',
          },
          '&[aria-selected="true"]': {
            backgroundColor: '#00000014 !important',
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#22A3AF',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-item': {
            padding: '12px !important',
          },
        }
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          flexWrap: 'nowrap',
          padding: '16px !important',
          minWidth: '200px !important',
          maxWidth: '560px',
          zIndex: 9999,
          borderRadius: '8px',
          color: '#2f4051',

          // Variant backgrounds via utility classes applied from notification component
          '&.success': {
            backgroundColor: '#A7FBD9',
            '& .MuiSnackbarContent-message .MuiSvgIcon-root': {
              color: '#007B4A',
            },
          },
          '&.error': {
            backgroundColor: '#FEC9CC',
            '& .MuiSnackbarContent-message .MuiSvgIcon-root': {
              color: '#BA0914',
            },
          },
          '&.info': {
            backgroundColor: '#C1F0F5',
            '& .MuiSnackbarContent-message .MuiSvgIcon-root': {
              color: '#2F4051',
            },
          },
          '&.warning': {
            backgroundColor: '#FFEFA1',
            '& .MuiSnackbarContent-message .MuiSvgIcon-root': {
              color: '#7D4600',
            },
          },
        },
        message: {
          padding: '0px',
        },
        action: {
          margin: '0px',
          padding: '0px',
          '& .MuiIconButton-root': {
            padding: '0px',
            marginLeft: '12px',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          width: '18px',
          height: '18px',
          marginRight: '8px',
          color: '#8B939A',
          borderRadius: 'none',
          '&:hover': {
            color: '#22A3AF',
          },
          '&:active': {
            color: '#056D78',
          },
          '&.Mui-disabled': {
            backgroundColor: '#DDE0E3',
            color: '#8B939A',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid #1233C1',
            outlineOffset: '1px',
            borderRadius: '2px',
          },
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#1B838B',
      contrastText: '#ffffff',
    },
    common: {
      black: '#212D38',
    },
    secondary: { main: DAMSTRA_SECONDARY, light: DAMSTRA_SECONDARY_LIGHT },
    error: {
      main: DAMSTRA_ERROR,
      dark: red[800],
    },
  },
});

export { GlobalTheme };
